import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import heroShoes from "@/assets/hero-shoes.jpg";
import {
  CheckCircle,
  Users,
  Trophy,
  Calendar,
  BarChart3,
  Settings,
  Zap,
  Shield,
  CreditCard,
  Mail,
  ClipboardList,
  MapPin,
  Briefcase,
  Truck,
  Package,
  UserCheck,
  ChevronRight,
  Star,
  ArrowRight,
  Globe,
  Layers,
  Target,
} from "lucide-react";

/* ─── Bullet ─── */
const Bullet = ({ children, light = false }: { children: React.ReactNode; light?: boolean }) => (
  <li className="flex items-start gap-3">
    <CheckCircle className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
    <span className={`leading-relaxed ${light ? "text-gray-300" : "text-gray-600"}`}>{children}</span>
  </li>
);

/* ═══════════════════════════════════════════
   HOME PAGE
   ═══════════════════════════════════════════ */
export default function Index() {
  return (
    <div className="min-h-screen bg-primary text-white overflow-x-hidden font-body">

      {/* ═══════ HERO ═══════ */}
      <section className="relative h-screen w-full overflow-hidden">
        <div
          className="absolute inset-0 w-full h-full"
          style={{ animation: "kenburns 12s ease-in-out forwards" }}
        >
          <img src={heroShoes} alt="RecruitLook event platform" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent z-10" />

        <div className="relative z-20 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-3xl space-y-6">
              <Badge className="bg-secondary/20 text-secondary border-secondary/40 font-heading text-xs tracking-widest uppercase">
                Event Operating System
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl leading-[1.05] text-white">
                Run tournaments on one platform
                <span className="text-secondary">&nbsp;— not ten spreadsheets.</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-2xl">
                Registration + payments, rosters, scheduling, brackets, live
                scoring, rankings &amp; leaderboards, check-in, communications,
                sponsorships, and a full operations suite. Built to scale,
                white-label, and sell as a standalone product.
              </p>
              <p className="text-sm text-secondary font-semibold tracking-wide">
                Waitlists + auto-promotion • Stripe checkout • Real-time brackets • Live scoring • Email + SMS • Sponsor placements • CSV exports
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Link to="/demo">
                  <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-black text-lg px-8 py-6 font-heading tracking-wide">
                    Request a Demo
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" className="bg-white/10 hover:bg-white/20 border border-white/30 text-white text-lg px-8 py-6 font-heading tracking-wide">
                    See Modules &amp; Pricing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ PLATFORM OVERVIEW ═══════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl lg:text-5xl mb-4 text-primary">
              PLATFORM <span className="text-secondary">OVERVIEW</span>
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto mb-8" />
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              The RecruitLook Event Platform is an end-to-end event engine for
              tournaments, showcases, leagues, and multi-day events. It combines
              registration + payments, brackets + scheduling, live scoring,
              rankings/leaderboards, and a full operations suite—all managed through
              a role-based admin dashboard and designed to be mobile-friendly.
            </p>
            <p className="text-gray-500 max-w-3xl mx-auto mt-4 leading-relaxed">
              It's built to replace the "spreadsheet chaos" that typically lives
              across Google Sheets, paper brackets, manual emails, Venmo/cash
              tracking, and disconnected tools.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════ WHAT IT DOES — 6 MODULE CARDS ═══════ */}
      <section className="py-24 bg-gradient-to-b from-primary to-primary/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-secondary/20 text-secondary border-secondary/40 font-heading text-xs tracking-widest uppercase mb-4">
              What It Does
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl text-white mb-4">
              EVERYTHING FROM <span className="text-secondary">SIGN-UP TO FINAL WHISTLE</span>
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto mb-4" />
            <p className="text-gray-300 text-lg">In one connected system.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: CreditCard,
                title: "Registration & Checkout",
                color: "from-blue-500 to-blue-700",
                items: [
                  "Multi-step registration wizard for individuals and teams",
                  "Pricing by division/age group with flexible tier logic",
                  "Spectator tickets + extras/add-ons (merch, meals, upsells)",
                  "Discount codes and promo validation with automatic totals",
                  "Custom forms per division with required-field validation",
                  "Division capacity controls with waitlist + auto-promotion",
                  "Pay Now / Pay Later supported with Stripe integration",
                ],
              },
              {
                icon: Zap,
                title: "Automated Fulfillment",
                color: "from-amber-500 to-amber-700",
                items: [
                  "Webhook-driven fulfillment triggered by payment",
                  "QR-coded tickets generated automatically",
                  "Purchased extras stored and tracked automatically",
                  "Coach team creation + invitation emails sent automatically",
                  "Email sequences based on Pay Now vs Pay Later path",
                ],
              },
              {
                icon: Trophy,
                title: "Brackets & Tournament Formats",
                color: "from-emerald-500 to-emerald-700",
                items: [
                  "Single Elim, Double Elim, Round Robin, Pool Play (with playoffs)",
                  "Pool assignment + automated match generation",
                  "Standings aggregation with live W/L/points tracking",
                  "Admin bracket setup dialog with seeding support",
                  "Live scoring updates that advance winners automatically",
                ],
              },
              {
                icon: Calendar,
                title: "Scheduling & Live Scoring",
                color: "from-purple-500 to-purple-700",
                items: [
                  "Court/field scheduling and time-slot management",
                  "Live scoring interface for game-day operations",
                  "Printable schedules and staff-friendly views",
                  "Calendar views (day/week/time-grid/list)",
                ],
              },
              {
                icon: BarChart3,
                title: "Rankings & Leaderboards",
                color: "from-red-500 to-red-700",
                items: [
                  "Database-driven scoring rules (no-code updates to scoring model)",
                  "Automatic point calculation + real-time leaderboard updates",
                  "Admin tools: manual entries, edit history, CSV import, team/logo management",
                  "Season and age-group segmentation",
                ],
              },
              {
                icon: Settings,
                title: "Operations Suite",
                color: "from-cyan-500 to-cyan-700",
                items: [
                  "Staffing: roles → directory → needs → assignments + gap analysis",
                  "Officials/Scorekeepers: coverage grids, availability, cert tracking",
                  "Travel: flights/hotels/cars board, budgets vs actuals",
                  "Inventory/Signage: warehouse tracking, venue allocations",
                  "Checklists & Notes: template-driven tasks, overdue alerts",
                ],
              },
            ].map((mod, i) => (
              <Card
                key={i}
                className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl group"
              >
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${mod.color} flex items-center justify-center shadow-lg`}>
                      <mod.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-heading text-lg text-white">{mod.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {mod.items.map((item, j) => (
                      <Bullet key={j} light>{item}</Bullet>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ DEEP DIVE: REGISTRATION ═══════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <Badge className="bg-primary/10 text-primary font-heading text-xs tracking-widest uppercase mb-4">
                Module 1
              </Badge>
              <h2 className="font-heading text-3xl lg:text-4xl text-primary mb-4">
                REGISTRATION <span className="text-secondary">&amp; CHECKOUT</span>
              </h2>
              <div className="w-16 h-1 bg-secondary mb-6" />
              <p className="text-gray-600 leading-relaxed mb-8">
                A modern, multi-step registration system supports both individual and team flows,
                with flexible configuration per event.
              </p>
              <ul className="space-y-3">
                {[
                  "Multi-step registration wizard (V2) for a guided signup experience",
                  "Dynamic pricing by age group/division (tiered pricing logic)",
                  "Discount / promo codes with validation + automatic calculation",
                  "Add-ons & extras during checkout (merch, meals, upsells)",
                  "Spectator tickets with thumbnail previews + direct purchase flow",
                  "Custom registration forms per division/tier with required-field validation",
                  "Special requests supported (optionally with fees per tier)",
                  "Capacity controls per division including waitlists and auto-promotion",
                  "Pay Now / Pay Later options with integrated Stripe checkout",
                ].map((t, i) => (
                  <Bullet key={i}>{t}</Bullet>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-primary/5 to-secondary/10 rounded-2xl p-8 border border-primary/10">
              <h4 className="font-heading text-lg text-primary mb-4">Admin Controls</h4>
              <ul className="space-y-3">
                {[
                  "View registrations in detail",
                  "Mark registrations paid/comped",
                  "Cancel/delete registrations",
                  "Filter by payment status (paid/unpaid/comped)",
                  "CSV export",
                ].map((t, i) => (
                  <Bullet key={i}>{t}</Bullet>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ DEEP DIVE: FULFILLMENT ═══════ */}
      <section className="py-24 bg-gradient-to-br from-primary via-primary to-primary/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="bg-secondary/20 text-secondary border-secondary/40 font-heading text-xs tracking-widest uppercase mb-4">
            Module 2
          </Badge>
          <h2 className="font-heading text-3xl lg:text-4xl text-white mb-4">
            AUTOMATED <span className="text-secondary">FULFILLMENT &amp; COMMUNICATIONS</span>
          </h2>
          <div className="w-16 h-1 bg-secondary mb-6" />
          <p className="text-gray-300 leading-relaxed max-w-2xl mb-10">
            Once payment occurs, the system triggers automatic fulfillment so staff doesn't need
            to manually reconcile or send follow-ups.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Zap, text: "Webhook-driven fulfillment tied to payment completion" },
              { icon: Shield, text: "QR-coded ticket generation for registrations / tickets" },
              { icon: Package, text: "Extras tracking (purchased add-ons are recorded automatically)" },
              { icon: Mail, text: "Coach team creation + coach invitation emails triggered on payment" },
              { icon: Users, text: "Email sequence enrollment based on Pay Now vs Pay Later path" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="h-10 w-10 rounded-lg bg-secondary/20 flex items-center justify-center shrink-0">
                  <item.icon className="h-5 w-5 text-secondary" />
                </div>
                <p className="text-gray-200 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ DEEP DIVE: BRACKETS ═══════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-primary/10 text-primary font-heading text-xs tracking-widest uppercase mb-4">
              Module 3
            </Badge>
            <h2 className="font-heading text-3xl lg:text-4xl text-primary mb-4">
              BRACKETS &amp; <span className="text-secondary">TOURNAMENT FORMATS</span>
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto mb-6" />
            <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
              The platform supports common tournament structures with real admin tooling.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Trophy, title: "Bracket Formats", desc: "Single Elimination, Double Elimination, Round Robin, Pool Play (with playoffs)" },
              { icon: Users, title: "Pool Play", desc: "Pool assignment + automated match generation for round-robin play" },
              { icon: BarChart3, title: "Standings", desc: "Standings aggregation with real-time win/loss/points tracking" },
              { icon: Settings, title: "Bracket Setup", desc: "Bracket setup + seeding tools via an admin generation dialog" },
              { icon: Zap, title: "Live Scoring", desc: "Live score updates with auto-advancing winners through the bracket" },
              { icon: Shield, title: "Audit Trail", desc: "Persistent audit/history support through a tournament service layer" },
            ].map((item, i) => (
              <Card key={i} className="border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                <CardContent className="p-6">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center mb-4 shadow-md group-hover:scale-110 transition-transform">
                    <item.icon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="font-heading text-lg text-primary mb-2">{item.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ DEEP DIVE: SCHEDULING ═══════ */}
      <section className="py-24 bg-gradient-to-r from-primary/95 to-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-secondary/20 text-secondary border-secondary/40 font-heading text-xs tracking-widest uppercase mb-4">
                Module 4
              </Badge>
              <h2 className="font-heading text-3xl lg:text-4xl text-white mb-4">
                SCHEDULING &amp; <span className="text-secondary">LIVE SCORING</span>
              </h2>
              <div className="w-16 h-1 bg-secondary mb-6" />
              <p className="text-gray-300 leading-relaxed mb-8">
                Event operators can manage scheduling and scoring with tools built for real-day execution.
              </p>
              <ul className="space-y-4">
                {[
                  "Court/field scheduling with time slot management",
                  "Live scoring interface (demo-ready scoring experience exists)",
                  "Printable schedules via a schedule modal + print support",
                  "Calendar views (day/week/time grid/list)",
                ].map((t, i) => (
                  <Bullet key={i} light>{t}</Bullet>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Calendar, label: "Day View" },
                { icon: ClipboardList, label: "Week View" },
                { icon: BarChart3, label: "Time Grid" },
                { icon: Globe, label: "List View" },
              ].map((v, i) => (
                <div key={i} className="bg-white/10 border border-white/20 rounded-xl p-6 text-center hover:bg-white/15 transition-colors">
                  <v.icon className="h-8 w-8 text-secondary mx-auto mb-2" />
                  <p className="font-heading text-sm text-white">{v.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ DEEP DIVE: RANKINGS ═══════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="bg-primary/10 text-primary font-heading text-xs tracking-widest uppercase mb-4">
            Module 5
          </Badge>
          <h2 className="font-heading text-3xl lg:text-4xl text-primary mb-4">
            POINTS, RANKINGS &amp; <span className="text-secondary">LEADERBOARDS</span>
          </h2>
          <div className="w-16 h-1 bg-secondary mb-6" />
          <p className="text-gray-600 leading-relaxed max-w-2xl mb-10">
            A configurable rankings engine supports seasons, divisions, and event-level scoring
            models—without needing code changes.
          </p>

          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <h4 className="font-heading text-lg text-primary mb-4">Scoring Engine</h4>
              <ul className="space-y-3">
                {[
                  "Database-driven scoring configuration (admin-adjustable rules)",
                  "Automatic point calculation based on win/loss outcomes",
                  "Point differential logic",
                  "'Blowout' thresholds (e.g., losses by ≥ X earn 0 points)",
                  "Bonus logic (e.g., losing to designated 'shoe teams' earns bonus points)",
                  "Real-time leaderboard updates that recalc when entries are added/edited/deleted",
                ].map((t, i) => (
                  <Bullet key={i}>{t}</Bullet>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-primary/5 to-secondary/10 rounded-2xl p-8 border border-primary/10">
              <h4 className="font-heading text-lg text-primary mb-4">Admin Hub</h4>
              <ul className="space-y-3">
                {[
                  "Manual entry with live preview",
                  "History with inline editing",
                  "CSV import for bulk ingestion",
                  "Team/logo management (logo updates propagate across displays)",
                  "Segmentation by season and age group",
                ].map((t, i) => (
                  <Bullet key={i}>{t}</Bullet>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ DEEP DIVE: OPERATIONS SUITE ═══════ */}
      <section className="py-24 bg-gradient-to-b from-primary to-primary/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-secondary/20 text-secondary border-secondary/40 font-heading text-xs tracking-widest uppercase mb-4">
              Module 6
            </Badge>
            <h2 className="font-heading text-3xl lg:text-4xl text-white mb-4">
              OPERATIONS <span className="text-secondary">SUITE</span>
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto mb-6" />
            <p className="text-gray-300 max-w-2xl mx-auto">
              Beyond competition management, the platform includes operational tooling usually
              handled in disconnected spreadsheets.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Package,
                title: "Inventory & Signage",
                color: "from-blue-500 to-blue-700",
                items: [
                  "Track equipment/banners/supplies across warehouses",
                  "Venue allocations + transfer/order workflows",
                  "Stock updates automatically on receipt",
                ],
              },
              {
                icon: Users,
                title: "Staffing",
                color: "from-emerald-500 to-emerald-700",
                items: [
                  "Workflow: roles → directory → needs → assignments",
                  "Pay rate defaults per role",
                  "Per-venue staffing requirements + assignment planning",
                  "Gap analysis shows what's still unfilled in real time",
                ],
              },
              {
                icon: Truck,
                title: "Travel Command Center",
                color: "from-purple-500 to-purple-700",
                items: [
                  "Event-specific board for flights/hotels/rental cars",
                  "Budget vs actual tracking",
                  "Staff home-location mapping for logistics",
                ],
              },
              {
                icon: UserCheck,
                title: "Officials & Scorekeepers",
                color: "from-red-500 to-red-700",
                items: [
                  "Venue-by-venue coverage grids with status updates",
                  "Availability tracking for scheduling",
                  "Certification and payment tracking",
                ],
              },
              {
                icon: ClipboardList,
                title: "Checklists & Team Notes",
                color: "from-amber-500 to-amber-700",
                items: [
                  "Template-driven task lists (pre / during / post event)",
                  "Progress tracking with overdue alerts",
                  "Chronological notes for coordination",
                ],
              },
              {
                icon: Briefcase,
                title: "Expenses & Budgets",
                color: "from-cyan-500 to-cyan-700",
                items: [
                  "Coming soon — positioned as an optional add-on roadmap item",
                ],
              },
            ].map((card, i) => (
              <Card key={i} className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300 hover:scale-[1.02] group">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center shadow-lg`}>
                      <card.icon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-heading text-lg text-white">{card.title}</h4>
                  </div>
                  <ul className="space-y-2">
                    {card.items.map((item, j) => (
                      <Bullet key={j} light>{item}</Bullet>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ MORE OF THE PLATFORM ═══════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-primary/10 text-primary font-heading text-xs tracking-widest uppercase mb-4">
              More Of The Platform
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl mb-4 text-primary">
              TEAMS, PEOPLE &amp; <span className="text-secondary">EVERYTHING ELSE</span>
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto mb-8" />
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Beyond competition management, the platform connects teams, athletes,
              communications, sponsors, and multi-org branding into one system.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Users,
                color: "from-blue-500 to-blue-700",
                title: "Teams, Rosters & Player Profiles",
                items: [
                  "Club → team → player identity spine with team history",
                  "Team create/edit wizards and per-team dashboards",
                  "AI-assisted roster upload and ingestion",
                  "Player claim invitations (athletes claim their roster spot)",
                  "Parent–athlete relationships",
                  "Master-team dedup and merge with audit logs",
                ],
              },
              {
                icon: UserCheck,
                color: "from-emerald-500 to-emerald-700",
                title: "Check-In & Credentials",
                items: [
                  "Team and athlete check-in",
                  "Coach and college-coach check-in tiers",
                  "Media credentials",
                  "Staff/admin check-in screen (QR-ready)",
                  "Timestamped, authorized check-in records",
                ],
              },
              {
                icon: Mail,
                color: "from-amber-500 to-amber-700",
                title: "Communications — Email & SMS",
                items: [
                  "Per-organization templates with tenant branding",
                  "Automated coach invites, registrant receipts, roster invites, admin alerts",
                  "Event broadcasts to all registrants",
                  "Twilio SMS / text messaging",
                  "Conditional, data-driven template rendering",
                ],
              },
              {
                icon: Star,
                color: "from-red-500 to-red-700",
                title: "Sponsorships & Advertising",
                items: [
                  "Sponsor placements on event, team, and public pages",
                  "Ad asset management and placement controls",
                  "Impression and click tracking",
                  "Sponsor and ad analytics dashboards",
                ],
              },
              {
                icon: ClipboardList,
                color: "from-purple-500 to-purple-700",
                title: "Live Sheets",
                items: [
                  "Shared live event sheets that update in real time",
                  "Scoped access tokens per sheet",
                  "A /my-sheets inbox for everyone involved",
                  "Edits propagate to event, team, and athlete pages",
                ],
              },
              {
                icon: Layers,
                color: "from-cyan-500 to-cyan-700",
                title: "Role-Based Dashboards",
                items: [
                  "Operator/admin command center",
                  "Coach dashboard with wallet + scheduling requests",
                  "Registrant dashboard (registrations, balances, receipts)",
                  "Athlete dashboard (schedule, check-in, status)",
                ],
              },
              {
                icon: Globe,
                color: "from-blue-500 to-blue-700",
                title: "White-Label, Multi-Org & Branding",
                items: [
                  "Multi-organization tenancy",
                  "Per-org Stripe Connect onboarding (charges route to the org)",
                  "Per-org templates, branding, and communications",
                  "Custom domains / subdomains",
                  "Organization members, roles, and credits",
                ],
              },
              {
                icon: Shield,
                color: "from-emerald-500 to-emerald-700",
                title: "RecruitLook Identity Bridge",
                items: [
                  "Single sign-on across RecruitLook + RLEvents",
                  "Shared athlete identity (one profile, both systems)",
                  "Rostered event athletes flow into the recruiting network",
                ],
              },
              {
                icon: MapPin,
                color: "from-amber-500 to-amber-700",
                title: "Hotels & Travel Partner",
                items: [
                  "Hotel property catalog + partner portal",
                  "RFP requests and bookings",
                  "Event–hotel assignments",
                ],
              },
              {
                icon: Briefcase,
                color: "from-red-500 to-red-700",
                title: "College Coach Portal & Packets",
                items: [
                  "Generated recruiting packets",
                  "Media credentials for coverage",
                  "College-coach access and athlete views",
                  "Connected to the RecruitLook recruiting network",
                ],
              },
              {
                icon: Target,
                color: "from-purple-500 to-purple-700",
                title: "Mobile App",
                items: [
                  "Schedules, scores, standings, and check-in on mobile",
                  "Real-time updates on game day",
                  "White-label theming per organization",
                ],
              },
              {
                icon: BarChart3,
                color: "from-cyan-500 to-cyan-700",
                title: "Analytics, Exports & Reporting",
                items: [
                  "Registration analytics and summaries",
                  "CSV / downloads for registrations, rosters, and reports",
                  "Sponsor and advertising analytics",
                  "Revenue and finance views",
                ],
              },
            ].map((card, i) => (
              <Card key={i} className="border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center shadow-lg`}>
                      <card.icon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-heading text-lg text-primary">{card.title}</h4>
                  </div>
                  <ul className="space-y-2">
                    {card.items.map((item, j) => (
                      <Bullet key={j}>{item}</Bullet>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ CROSS-CUTTING ═══════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl lg:text-4xl text-primary mb-4">
              CROSS-CUTTING <span className="text-secondary">CAPABILITIES</span>
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto mb-6" />
            <p className="text-gray-600 max-w-2xl mx-auto">These apply across all modules.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Shield, text: "Role-based access (admin control + scoped coach/athlete views)" },
              { icon: Globe, text: "Mobile-responsive UI across the platform" },
              { icon: Layers, text: "CSV exports for registrations, rosters, and reports" },
              { icon: CreditCard, text: "Payments + fulfillment designed around Stripe + webhooks" },
              { icon: Target, text: "Built to scale across multiple events, divisions, teams, and seasons" },
              { icon: Star, text: "White-label ready — package as a standalone sellable system" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shrink-0 shadow-md">
                  <item.icon className="h-6 w-6 text-white" />
                </div>
                <p className="text-gray-700 leading-relaxed font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ WHO IT'S FOR ═══════ */}
      <section className="py-24 bg-gradient-to-br from-primary via-primary to-primary/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl lg:text-4xl text-white mb-4">
              WHO IT'S <span className="text-secondary">FOR</span>
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              "Tournament directors & showcase operators",
              "League organizers",
              "Sports facilities / venue operators",
              "Clubs and event production teams",
              "Organizations building a white-label event product",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/10 border border-white/20 rounded-xl p-5 hover:bg-white/15 transition-colors">
                <ChevronRight className="h-5 w-5 text-secondary shrink-0" />
                <span className="text-white font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ WHY IT'S DIFFERENT ═══════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl lg:text-4xl text-primary mb-4">
              WHY IT'S <span className="text-secondary">DIFFERENT</span>
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Not "just a registration form."</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-8">
            {[
              {
                icon: ArrowRight,
                color: "from-blue-500 to-blue-700",
                title: "One connected workflow",
                desc: "Registration → bracket → scoring → rankings → ops — all in one place.",
              },
              {
                icon: Zap,
                color: "from-amber-500 to-amber-700",
                title: "Automation reduces staffing load",
                desc: "Eliminates manual errors and post-payment busywork.",
              },
              {
                icon: Layers,
                color: "from-emerald-500 to-emerald-700",
                title: "Modular",
                desc: "Enable only what a buyer needs. Mix-and-match core modules.",
              },
              {
                icon: Settings,
                color: "from-purple-500 to-purple-700",
                title: "Built for real event-day operations",
                desc: "Staffing, officials, travel, inventory, and checklists — not afterthoughts.",
              },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-5 p-6 rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className={`h-14 w-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shrink-0 shadow-lg`}>
                  <item.icon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h4 className="font-heading text-xl text-primary mb-2">{item.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ MODULES & ADD-ONS ═══════ */}
      <section className="py-24 bg-gradient-to-b from-primary to-primary/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-secondary/20 text-secondary border-secondary/40 font-heading text-xs tracking-widest uppercase mb-4">
              Packaging
            </Badge>
            <h2 className="font-heading text-3xl lg:text-4xl text-white mb-4">
              MODULES &amp; <span className="text-secondary">ADD-ONS</span>
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto mb-6" />
            <p className="text-gray-300 max-w-2xl mx-auto">
              A clean menu for licensing, reseller deals, or tiered subscriptions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                letter: "A",
                title: "Registration Core",
                items: [
                  "Multi-step registration (team + individual)",
                  "Division/tier pricing logic",
                  "Discount codes",
                  "Custom forms per division",
                  "Capacity + waitlist",
                  "Admin management + CSV export",
                ],
              },
              {
                letter: "B",
                title: "Payments & Fulfillment",
                items: [
                  "Stripe checkout",
                  "Pay Now / Pay Later flows",
                  "Payment plans",
                  "Webhook fulfillment automation",
                  "QR tickets + extras records",
                  "Coach invites",
                  "Refunds / comps",
                ],
              },
              {
                letter: "C",
                title: "Teams & Rosters",
                items: [
                  "Identity spine",
                  "Roster tools",
                  "AI roster upload",
                  "Player claims",
                  "Team dedup / merge",
                ],
              },
              {
                letter: "D",
                title: "Tournament Engine",
                items: [
                  "Single / Double Elim, Round Robin, Pool Play + playoffs",
                  "Seeding",
                  "Standings",
                  "Auto-advance scoring",
                ],
              },
              {
                letter: "E",
                title: "Scheduling & Scoring",
                items: [
                  "Court/field scheduling",
                  "Conflict analysis",
                  "Live scoring",
                  "Printable schedules",
                  "Calendar views",
                ],
              },
              {
                letter: "F",
                title: "Rankings & Circuits",
                items: [
                  "Configurable scoring",
                  "Season-long circuits",
                  "Real-time leaderboards",
                  "Admin entry + CSV",
                  "Logo management",
                ],
              },
              {
                letter: "G",
                title: "Check-In & Credentials",
                items: [
                  "Team / athlete / coach / college-coach check-in",
                  "Media credentials",
                  "QR",
                ],
              },
              {
                letter: "H",
                title: "Communications",
                items: [
                  "Email + SMS",
                  "Per-org templates",
                  "Broadcasts",
                  "Automated lifecycle messaging",
                ],
              },
              {
                letter: "I",
                title: "Sponsorships & Advertising",
                items: [
                  "Sponsor placements",
                  "Ad assets",
                  "Impression / click tracking",
                  "Analytics",
                ],
              },
              {
                letter: "J",
                title: "Live Sheets",
                items: [
                  "Shared real-time sheets",
                  "Scoped tokens",
                  "/my-sheets inbox",
                ],
              },
              {
                letter: "K",
                title: "Dashboards",
                items: [
                  "Operator portal",
                  "Coach (wallet + requests)",
                  "Registrant portal",
                  "Athlete portal",
                ],
              },
              {
                letter: "L",
                title: "Operations Suite",
                items: [
                  "Staffing",
                  "Officials / scorekeepers",
                  "Travel",
                  "Inventory / signage",
                  "Checklists",
                  "Expenses (roadmap)",
                ],
              },
              {
                letter: "M",
                title: "Hotels & Travel",
                items: [
                  "Hotel partner portal",
                  "RFPs",
                  "Bookings",
                  "Event assignments",
                ],
              },
              {
                letter: "N",
                title: "White-Label & Multi-Org",
                items: [
                  "Multi-org tenancy",
                  "Per-org Stripe Connect",
                  "Custom domains",
                  "Per-tenant branding",
                ],
              },
            ].map((mod, i) => (
              <Card key={i} className="bg-white/5 border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-[1.02]">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="h-10 w-10 rounded-full bg-gradient-to-br from-secondary to-secondary/80 text-black flex items-center justify-center font-heading text-lg shadow-md">
                      {mod.letter}
                    </span>
                    <h4 className="font-heading text-lg text-white">{mod.title}</h4>
                  </div>
                  <ul className="space-y-2">
                    {mod.items.map((item, j) => (
                      <Bullet key={j} light>{item}</Bullet>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FAQs ═══════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl lg:text-4xl text-primary mb-4">
              FREQUENTLY ASKED <span className="text-secondary">QUESTIONS</span>
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                q: "Can we white-label it?",
                a: "Yes — designed to be packaged as a standalone sellable system.",
              },
              {
                q: "Does it support teams and individuals?",
                a: "Yes — both flows are fully supported with separate registration wizards.",
              },
              {
                q: "Can we run pool play and playoffs?",
                a: "Yes — pool play match generation + standings + bracket advancement are all built in.",
              },
              {
                q: "Do you support Pay Later?",
                a: "Yes — separate Pay Now vs Pay Later flows, including automation and admin controls.",
              },
              {
                q: "Do you send text messages?",
                a: "Yes — email and SMS, including automated invites, receipts, and broadcasts.",
              },
              {
                q: "Can sponsors advertise on our events?",
                a: "Yes — sponsor placements and ad units with impression/click analytics.",
              },
              {
                q: "Can we run a season-long circuit or series?",
                a: "Yes — multi-event circuits with configurable scoring and live leaderboards.",
              },
              {
                q: "Can we run multiple organizations?",
                a: "Yes — multi-org tenancy with per-org payments, branding, and domains.",
              },
              {
                q: "Is there a mobile app?",
                a: "On the roadmap — a tournament-goer app for schedules, scores, and check-in.",
              },
            ].map((faq, i) => (
              <div key={i} className="border-l-4 border-secondary pl-6 py-2">
                <h4 className="font-heading text-lg text-primary mb-2">{faq.q}</h4>
                <p className="text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ SELL SHEET / CTA ═══════ */}
      <section className="py-24 bg-gradient-to-br from-primary via-primary to-primary/90 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-secondary rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="font-heading text-3xl lg:text-4xl text-white">
            READY TO <span className="text-secondary">GET STARTED?</span>
          </h2>
          <div className="w-24 h-1 bg-secondary mx-auto" />
          <p className="text-gray-300 text-lg leading-relaxed max-w-3xl mx-auto">
            This event platform is a complete operating system for tournaments and
            showcases—covering registration (teams + individuals), payments,
            discounts, add-ons, spectator tickets, division capacity + waitlists,
            automated email and fulfillment, bracket generation, scheduling, live
            scoring, and real-time standings/leaderboards. It also includes a robust
            operations suite for staffing, travel, officials, inventory, and checklist
            execution—so organizers can run events from one place instead of
            spreadsheets and fragmented tools.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link to="/demo">
              <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-black text-lg px-10 py-6 font-heading tracking-wide shadow-xl">
                Request a Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
